if (!customElements.get("shoppable-video")) {
  class ShoppableVideo extends HTMLElement {
    constructor() {
      super();
    }

    bindMethods() {
      this.togglePlayPause = this.togglePlayPause.bind(this);
      this.toggleVideoIcon = this.toggleVideoIcon.bind(this);
      this.playVideo = this.playVideo.bind(this);

      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
      this.handleScroll = this.handleScroll.bind(this);

      this.clickRewindButton = this.clickRewindButton.bind(this);
      this.animateScrollToProduct = this.animateScrollToProduct.bind(this);

      this.setupVideoObserver = this.setupVideoObserver.bind(this);
      
      // ✨ 마우스 드래그 핸들러
      this.handleMouseDown = this.handleMouseDown.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handleMouseUp = this.handleMouseUp.bind(this);
      this.handleMouseMove = this.handleMouseMove.bind(this);
    }

    setupElements() {
      try {
        this.productsInfo = JSON.parse(this.dataset.products || "{}");
      } catch (e) {
        this.productsInfo = {};
      }
      this.productsInfoArr = Object.entries(this.productsInfo);

      this.video = this.querySelector("video");

      if (this.video) {
        this.video.preload = "none";
        this.video.setAttribute("preload", "none");
      }

      this.productsContainer = this.querySelector(".shoppable-video__products");
      this.controlButton = this.querySelector(".shoppable-video__control-button");
      this.products = this.productsContainer ? this.productsContainer.querySelectorAll(".shoppable-video__product") : [];
      this.emptyProduct = this.productsContainer ? this.productsContainer.querySelector(".shoppable-video__empty-product") : null;
      this.playButtons = this.productsContainer ? this.productsContainer.querySelectorAll(".shoppable-video__rewind-button") : [];

      this.playButtonsListeners = [];
      this.isTouching = false;
      this.isDragging = false; // ✨ 드래그 상태 확인
      this.lastScrollLeft = 0;
      this.ticking = false;

      if (this.productsContainer && this.emptyProduct) {
        this.calculateEmptyProductWidth();
        this.setContainerMargin();
      }
    }

    addEventListeners() {
      if (this.controlButton) {
        this.controlButton.addEventListener("click", this.togglePlayPause);
      }
      if (this.video) {
        this.video.addEventListener("click", this.togglePlayPause);
        this.video.addEventListener("play", this.toggleVideoIcon);
        this.video.addEventListener("pause", this.toggleVideoIcon);
      }
      
      if (this.productsContainer) {
        // 모바일 터치
        this.productsContainer.addEventListener("touchstart", this.handleTouchStart, { passive: true });
        this.productsContainer.addEventListener("touchend", this.handleTouchEnd);
        this.productsContainer.addEventListener("scroll", this.handleScroll, { passive: true });
        
        // ✨ 데스크탑 마우스 드래그 (Swipe)
        this.productsContainer.addEventListener("mousedown", this.handleMouseDown);
        this.productsContainer.addEventListener("mouseleave", this.handleMouseLeave);
        this.productsContainer.addEventListener("mouseup", this.handleMouseUp);
        this.productsContainer.addEventListener("mousemove", this.handleMouseMove);
      }

      this.playButtons.forEach((btn) => {
        const boundClickRewindButton = this.clickRewindButton.bind(this, btn);
        btn.addEventListener("click", boundClickRewindButton);
        this.playButtonsListeners.push({ btn, boundClickRewindButton });
      });
      this.handleResize = () => {
        if (this.productsContainer && this.emptyProduct) {
          this.calculateEmptyProductWidth();
          this.setContainerMargin();
        }
      };
      window.addEventListener("resize", this.handleResize);
    }

    /* =========================================
       ✨ 데스크탑 마우스 드래그 로직 (Swipe)
       ========================================= */
    handleMouseDown(e) {
      this.isDragging = true;
      this.startX = e.pageX - this.productsContainer.offsetLeft;
      this.scrollLeftStart = this.productsContainer.scrollLeft;
      // 드래그 시작 시 CSS 커서 변경 효과를 위해 클래스 추가 가능
      this.productsContainer.classList.add('active'); 
    }

    handleMouseLeave() {
      this.isDragging = false;
      this.productsContainer.classList.remove('active');
    }

    handleMouseUp() {
      this.isDragging = false;
      this.productsContainer.classList.remove('active');
    }

    handleMouseMove(e) {
      if (!this.isDragging) return;
      e.preventDefault(); // 텍스트 선택 등 기본 동작 방지
      const x = e.pageX - this.productsContainer.offsetLeft;
      const walk = (x - this.startX) * 2; // ✨ 스크롤 속도 조절 (숫자가 클수록 빠름)
      this.productsContainer.scrollLeft = this.scrollLeftStart - walk;
    }

    /* =========================================
       모바일 터치 및 공통 스크롤 로직
       ========================================= */
    handleTouchStart(e) {
      if (!this.productsContainer) return;
      this.isTouching = true;
      this.startX = e.touches[0].pageX;
      this.startY = e.touches[0].pageY;
    }

    handleTouchEnd() {
      this.isTouching = false;
    }

    handleScroll() {
      if (!this.productsContainer) return;
      this.lastScrollLeft = this.productsContainer.scrollLeft;

      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this.checkDominantProductElement();
          this.ticking = false;
        });
        this.ticking = true;
      }
    }

    checkDominantProductElement() {
      if (!this.productsContainer || !this.products?.length) return;
      let dominantProductIndex = -1;
      let maxVisibleWidth = -1;
      const containerRect = this.productsContainer.getBoundingClientRect();

      this.products.forEach((product, index) => {
        const { left, right } = product.getBoundingClientRect();
        const visibleWidth = Math.min(containerRect.right, right) - Math.max(containerRect.left, left);
        if (visibleWidth > maxVisibleWidth) {
          maxVisibleWidth = visibleWidth;
          dominantProductIndex = index;
        }
      });

      if (dominantProductIndex !== -1 && maxVisibleWidth / this.productsContainer.offsetWidth > 0.2) {
        this.dominantProductIndex = dominantProductIndex;
      }
    }

    calculateEmptyProductWidth() {
      if (!this.productsContainer || !this.emptyProduct) return;
      const allProducts = this.productsContainer.querySelectorAll(".shoppable-video__product");
      if (allProducts.length > 1) {
        const firstRealProduct = allProducts[1];
        const firstProductWidth = firstRealProduct.offsetWidth || 0;
        this.gap = parseFloat(window.getComputedStyle(this.productsContainer).getPropertyValue("gap")) || 0;
        this.emptyProduct.style.width = `calc(100% - ${firstProductWidth}px - ${this.gap}px)`;
      } else {
        this.emptyProduct.style.width = "0px";
      }
    }

    setContainerMargin() {
      if (!this.productsContainer || !this.products?.length) return;
      let maxHeight = 0;
      this.products.forEach((element) => {
        const elementHeight = element.offsetHeight;
        if (elementHeight > maxHeight) maxHeight = elementHeight;
      });
      const marginValue = window.innerWidth > 900 ? 46 : 34;
      this.productsContainer.style.marginTop = `-${maxHeight + marginValue}px`;
    }

    setupVideoObserver() {
      if (!this.video) return;
      if ("IntersectionObserver" in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const playPromise = this.video.play();
              if (playPromise !== undefined) {
                playPromise.catch(() => {
                  this.video.muted = true;
                  this.video.play();
                });
              }
            } else {
              this.video.pause();
            }
          });
        }, { threshold: 0.2 });
        observer.observe(this.video);
        this.videoObserver = observer;
      } else {
        this.video.play();
      }
    }

    toggleVideoIcon() {
      if (!this.controlButton || !this.video) return;
      const isPlaying = !this.video.paused;
      this.controlButton.classList.toggle("shoppable-video__control-button--play", isPlaying);
      this.controlButton.classList.toggle("shoppable-video__control-button--pause", !isPlaying);
    }

    togglePlayPause(e) {
      if (e) e.preventDefault();
      const videoElement = this.video;
      if (!videoElement) return;
      if (videoElement.paused || videoElement.ended) {
        videoElement.play();
      } else {
        videoElement.pause();
      }
    }

    playVideo() {
      const videoElement = this.video;
      if (!videoElement) return;
      if (videoElement.paused || videoElement.ended) {
        videoElement.play();
      }
    }

    clickRewindButton(btn) {
      const productIndex = Number(btn.dataset.productIndex);
      if (Number.isNaN(productIndex)) return;
      this.animateScrollToProduct(productIndex);
    }

    animateScrollToProduct(productIndex) {
      if (!this.productsContainer || !this.products?.length) return;
      const target = this.products[productIndex];
      if (!target) return;
      const startPosition = this.productsContainer.scrollLeft;
      const endPosition = target.offsetLeft;
      const duration = 350;
      this.animateScroll(startPosition, endPosition, duration);
    }

    animateScroll(startPosition, endPosition, duration) {
      const start = performance.now();
      const animate = (time) => {
        const timeElapsed = time - start;
        const progress = Math.min(timeElapsed / duration, 1);
        const currentPosition = startPosition + (endPosition - startPosition) * this.easeOutSine(progress);
        this.productsContainer.scrollLeft = currentPosition;
        if (timeElapsed < duration) {
          requestAnimationFrame(animate);
        } else {
          this.productsContainer.scrollLeft = endPosition;
        }
      };
      requestAnimationFrame(animate);
    }

    easeOutSine(t) {
      return Math.sin((t * Math.PI) / 2);
    }

    connectedCallback() {
      this.setupElements();
      this.bindMethods();
      this.addEventListeners();
      this.setupVideoObserver();
    }

    disconnectedCallback() {
      if (this.controlButton) this.controlButton.removeEventListener("click", this.togglePlayPause);
      if (this.video) {
        this.video.removeEventListener("click", this.togglePlayPause);
        this.video.removeEventListener("play", this.toggleVideoIcon);
        this.video.removeEventListener("pause", this.toggleVideoIcon);
      }
      if (this.productsContainer) {
        this.productsContainer.removeEventListener("touchstart", this.handleTouchStart);
        this.productsContainer.removeEventListener("touchend", this.handleTouchEnd);
        this.productsContainer.removeEventListener("scroll", this.handleScroll);
        
        this.productsContainer.removeEventListener("mousedown", this.handleMouseDown);
        this.productsContainer.removeEventListener("mouseleave", this.handleMouseLeave);
        this.productsContainer.removeEventListener("mouseup", this.handleMouseUp);
        this.productsContainer.removeEventListener("mousemove", this.handleMouseMove);
      }
      this.playButtonsListeners.forEach(({ btn, boundClickRewindButton }) => {
        btn.removeEventListener("click", boundClickRewindButton);
      });
      if (this.handleResize) window.removeEventListener("resize", this.handleResize);
      if (this.videoObserver) {
        this.videoObserver.disconnect();
        this.videoObserver = null;
      }
    }
  }

  customElements.define("shoppable-video", ShoppableVideo);
}